import { useEffect, useState, useRef } from 'react';
import CollaborationHandler from '@/bussiness/CollaborationHandler';
import { wsUrl } from '@/pages/Collaboration/constant';
import useLoginStore from '@/store/login.store';
import { VISITOR_TOKEN_STORAGE_KEY } from '@/constants/user';
import { TOKEN_KEY } from '@/config';

/**
 * 专门处理移动端熄屏后 WebSocket 重连的 Hook
 * 监听页面可见性变化，在亮屏后检测连接状态
 */
export const useWebSocketReconnect = (streamId: string | null) => {
  const [readyState, setReadyState] = useState(1);
  const [dataNow, setDataNow] = useState(new Date().getTime());
  const { isVisitor } = useLoginStore();
  const visibilityCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingActiveRef = useRef(false);

  // 开始轮询连接状态
  const startPolling = () => {
    // 避免重复启动轮询
    if (isPollingActiveRef.current) {
      return;
    }

    isPollingActiveRef.current = true;
    console.log('开始WebSocket状态轮询，每3秒检查一次');

    // 立即执行一次检查
    checkConnectionStatus();

    // 设置3秒间隔轮询
    pollingIntervalRef.current = setInterval(checkConnectionStatus, 3000);

    // 设置30秒后自动停止轮询
    pollingTimeoutRef.current = setTimeout(() => {
      console.log('轮询超时（30秒），自动停止');
      stopPolling();
    }, 30000);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    isPollingActiveRef.current = false;
    console.log('WebSocket状态轮询已停止');
  };

  // 轮询检查连接状态
  const checkConnectionStatus = async () => {
    try {
      const readyState = await CollaborationHandler.getReadyState();
      setDataNow(new Date().getTime());
      setReadyState(readyState as number);

      // 如果状态为3（已关闭）或页面隐藏，停止轮询
      if (readyState === 3 || document.visibilityState === 'hidden') {
        console.log('检测到连接已关闭或页面隐藏，停止轮询');
        stopPolling();
      }
    } catch (error) {
      console.error('检查连接状态失败:', error);
    }
  };

  // 监听页面可见性变化
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('页面变为可见，开始WebSocket状态轮询');
        startPolling();
      } else {
        console.log('页面变为隐藏，停止轮询');
        stopPolling();
        // 清除检查超时
        if (visibilityCheckTimeoutRef.current) {
          clearTimeout(visibilityCheckTimeoutRef.current);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopPolling();
      if (visibilityCheckTimeoutRef.current) {
        clearTimeout(visibilityCheckTimeoutRef.current);
      }
    };
  }, []);

  // 手动重连函数
  const handleReconnect = () => {
    if (!streamId) return;

    console.log('用户手动触发重连');
    setReadyState(0);

    // 停止当前轮询
    stopPolling();

    // 重新初始化 WebSocket 连接
    const token = isVisitor
      ? localStorage.getItem(VISITOR_TOKEN_STORAGE_KEY)
      : localStorage.getItem(TOKEN_KEY);
    const authorization = isVisitor ? `${token}` : `Bearer ${token}`;
    const url = wsUrl + '?authorization=' + authorization;

    // 停止当前连接
    CollaborationHandler.stopSocket();

    // 重新开始连接
    setTimeout(async () => {
      CollaborationHandler.initSocket(streamId, url);
      // 重连后开始新的轮询
      if (document.visibilityState === 'visible') {
        startPolling();
      }
    }, 1000);
  };

  return {
    readyState,
    dataNow,
    handleReconnect,
    startPolling,
    stopPolling,
  };
};

